import HistoryContent from 'components/history/HistoryContent';
import useParkingHistoryFilter from 'hooks/useParkingHistoryFilter';
import useReadStoppedSessionsForHistory from '../../hooks/useReadStoppedSessionsForHistory';
import useReadReceipts from 'hooks/useReadReceipts';
import { concat } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useRetryPaymentStore } from 'stores/retryPaymentStore';
import {
  ChargingSession,
  ParkingSession,
  Receipt,
} from 'types/generatedSchemaTypes';
import {
  chargingTypes,
  filterParkingOrChargingSessions,
  filterReceiptData,
  parkingTypes,
} from 'utils/parkUtils';

// Hoisted so the array reference is stable across renders — passing a fresh
// `concat(...)` into useReadReceipts caused its initialInput to change identity
// every render and re-trigger the refetch effect.
const PARKING_AND_CHARGING_RECEIPT_TYPES = concat(parkingTypes, chargingTypes);

const ParkingHistoryScreen: React.FC = () => {
  const { parkingType: parkingTypeFilter } = useParkingHistoryFilter();
  const {
    data: parkingAndChargingReceiptData,
    loading: parkingAndChargingReceiptsLoading,
    error: parkingAndChargingReceiptError,
    fetchMoreCallback: fetchMoreReceiptsCallback,
    resetPage,
    refetch: refetchReceipts,
  } = useReadReceipts(PARKING_AND_CHARGING_RECEIPT_TYPES);

  const {
    parkingSessions,
    sessionsLoading,
    chargingSessions,
    isLast,
    fetchMoreCallback: fetchMoreSessions,
  } = useReadStoppedSessionsForHistory();

  const successfulPaymentCount = useRetryPaymentStore(
    (s) => s.successfulPaymentCount
  );

  // Refetch receipts on every retry that transitions to Success so the
  // newly-paid session moves from Failed payments to the Receipts tab.
  useEffect(() => {
    if (successfulPaymentCount > 0) {
      refetchReceipts();
    }
  }, [successfulPaymentCount, refetchReceipts]);

  // This contains sessions without a receipt object, meaning either the payment failed,
  // or there was no reason to create a payment because the session price was zero,
  // or the session was paid by a corporate account or pooling.
  const sessionsWithoutPayment = useMemo(
    () =>
      filterParkingOrChargingSessions(
        [...parkingSessions, ...chargingSessions] as ParkingSession[] &
          ChargingSession[],
        parkingTypeFilter
      ),
    [chargingSessions, parkingSessions, parkingTypeFilter]
  );

  useEffect(() => {
    resetPage();
  }, [resetPage]);

  if (parkingAndChargingReceiptError) {
    return <></>;
  }

  return (
    <HistoryContent
      fetchMore={fetchMoreReceiptsCallback}
      failedSessions={
        (sessionsWithoutPayment || []) as ParkingSession[] & ChargingSession[]
      }
      sessionsFetchMore={fetchMoreSessions}
      sessionsIsLast={isLast || false}
      sessionsLoading={sessionsLoading}
      loading={parkingAndChargingReceiptsLoading || sessionsLoading}
      isLast={parkingAndChargingReceiptData?.last || false}
      receiptData={filterReceiptData(
        (parkingAndChargingReceiptData?.content || []) as Receipt[],
        parkingTypeFilter
      )}
    />
  );
};

export default ParkingHistoryScreen;
